import { useState, useRef, useEffect, useCallback } from 'react';
import type { CatProduct } from '@/mocks/categoryProducts';

interface SearchBarProps {
  products: CatProduct[];
  onResultsChange: (filtered: CatProduct[] | null) => void;
  initialQuery?: string;
}

const ALL_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '28', '30', '32', '34', '36', '38'];

export default function SearchBar({ products, onResultsChange, initialQuery = '' }: SearchBarProps) {
  const [query, setQuery] = useState(initialQuery);
  const [selectedSize, setSelectedSize] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<CatProduct[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Available sizes from current products
  const availableSizes = Array.from(
    new Set(products.flatMap((p) => p.sizes))
  ).filter((s) => ALL_SIZES.includes(s)).sort((a, b) => ALL_SIZES.indexOf(a) - ALL_SIZES.indexOf(b));

  const filterProducts = useCallback(
    (q: string, size: string) => {
      const trimmed = q.trim().toLowerCase();
      if (!trimmed && !size) {
        onResultsChange(null); // null = show all
        setSuggestions([]);
        return;
      }

      const filtered = products.filter((p) => {
        const matchesName = trimmed ? p.name.toLowerCase().includes(trimmed) : true;
        const matchesSize = size ? p.sizes.includes(size) : true;
        return matchesName && matchesSize && !p.outOfStock;
      });

      onResultsChange(filtered);

      // Suggestions: top 5 matching products by name
      if (trimmed) {
        setSuggestions(
          products
            .filter((p) => p.name.toLowerCase().includes(trimmed) && !p.outOfStock)
            .slice(0, 5)
        );
      } else {
        setSuggestions([]);
      }
    },
    [products, onResultsChange]
  );

  useEffect(() => {
    filterProducts(query, selectedSize);
  }, [query, selectedSize, filterProducts]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSuggestionClick = (product: CatProduct) => {
    setQuery(product.name);
    setIsFocused(false);
    filterProducts(product.name, selectedSize);
  };

  const handleClear = () => {
    setQuery('');
    setSelectedSize('');
    onResultsChange(null);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const isActive = query.trim() || selectedSize;

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Main search input */}
      <div
        className={`flex items-center gap-2 bg-white dark:bg-slate-800 rounded-2xl border-2 px-3 py-2.5 transition-all ${
          isFocused ? 'border-orange-400 shadow-lg shadow-orange-100 dark:shadow-orange-900/20' : 'border-gray-200 dark:border-slate-600'
        }`}
      >
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <i className={`ri-search-line text-base ${isFocused ? 'text-orange-500' : 'text-gray-400 dark:text-gray-500'}`}></i>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder="Search products..."
          className="flex-1 bg-transparent outline-none text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 min-w-0"
        />

        {isActive && (
          <button
            onClick={handleClear}
            className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 dark:bg-slate-600 hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-xs text-gray-600 dark:text-gray-400"></i>
          </button>
        )}
      </div>

      {/* Size filter chips */}
      {availableSizes.length > 0 && (
        <div
          className="flex gap-1.5 mt-2 overflow-x-auto pb-1"
          style={{ scrollbarWidth: 'none' }}
        >
          {availableSizes.map((size) => (
            <button
              key={size}
              onClick={() => setSelectedSize((prev) => (prev === size ? '' : size))}
              className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                selectedSize === size
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-600 hover:border-orange-300 hover:text-orange-500'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      )}

      {/* Active filter badge */}
      {isActive && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {query.trim() && (
            <div className="flex items-center gap-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-400 text-xs font-semibold px-2.5 py-1 rounded-full">
              <i className="ri-search-line text-[10px]"></i>
              &quot;{query}&quot;
              <button onClick={() => setQuery('')} className="ml-0.5 cursor-pointer hover:text-red-500">
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          )}
          {selectedSize && (
            <div className="flex items-center gap-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-400 text-xs font-semibold px-2.5 py-1 rounded-full">
              <i className="ri-ruler-line text-[10px]"></i>
              Size: {selectedSize}
              <button onClick={() => setSelectedSize('')} className="ml-0.5 cursor-pointer hover:text-red-500">
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Suggestions dropdown */}
      {isFocused && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-600 shadow-xl z-50 overflow-hidden">
          <div className="px-3 pt-2 pb-1">
            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-wider">Suggestions</p>
          </div>
          {suggestions.map((product) => (
            <button
              key={product.id}
              onMouseDown={() => handleSuggestionClick(product)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors cursor-pointer text-left"
            >
              {/* Product thumbnail */}
              <div className="w-10 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-700 shrink-0">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 dark:text-gray-200 font-semibold text-xs leading-tight truncate">{product.name}</p>
                <p className="text-orange-500 font-bold text-xs mt-0.5">₹{product.price}</p>
                <div className="flex gap-0.5 mt-0.5 flex-wrap">
                  {product.sizes.slice(0, 5).map((s) => (
                    <span key={s} className="text-[9px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-slate-700 px-1 rounded">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              <i className="ri-arrow-right-s-line text-gray-300 dark:text-gray-500 shrink-0"></i>
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {isFocused && query.trim() && suggestions.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-600 shadow-xl z-50 px-4 py-4 text-center">
          <i className="ri-search-line text-gray-300 dark:text-gray-500 text-2xl"></i>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">No products found for &quot;{query}&quot;</p>
        </div>
      )}
    </div>
  );
}
